import { Money } from '__generated__/types';
import { ProviderOrderConformityCheck } from 'collections/providerOrderConstants';
import { IAttachedFile, MeasurementProtocols } from 'interfaces';
import { MeasurementMarkFragment } from 'utils/graphql/fragments/__generated__/measurementMark';
import { DealQualityControlQuery } from 'utils/graphql/queries/__generated__/dealQualityControl';

import { IDealLot } from './dealLot';
import { IDealPart } from './dealPart';

export type IQCPassedType = null | 'ok' | 'issue' | 'not_ok';

export interface QCPositionProvider {
  id: number;
  number: string;
  conformityCheck?: string | null;
  deadline: string;
  state: string;
  provider?: {
    id: number;
    legalName: string;
  };
}

export interface QCPositionPart {
  id: number;
  name: string;
  position?: number;
  material?: string;
  postProcessing?: string;
  customerNote?: string;
  isExpress?: boolean;
  preQuoted?: boolean;
  measurementProtocol?: MeasurementProtocols;
  productionRemark?: string;
  internalComment?: string;
  preQuotedBy?: {
    id: number;
    name: string;
  };
  preQuotedComment?: string;
  samplesNeeded?: boolean;
  samplesQuantity?: number;
  samplesComment?: string;
  files?: IAttachedFile[];
}

export enum QCReportType {
  QualityTestReport = 'quality_test_report',
  QualityIssuesReport = 'quality_issues_report',
  MeasurementReport = 'measurement_report',
  FirstArticleReport = 'first_article_report',
  VQC = 'vqc',
}

export enum QualityControlTechVerdict {
  Ok = 'ok',
  LowRisk = 'low_risk',
  MediumRisk = 'medium_risk',
  HighRisk = 'high_risk',
  NotOk = 'not_ok',
}

export enum QualityControlBusinessVerdict {
  Ship = 'ship',
  ShipDiscounted = 'ship_discounted',
  Store = 'store',
  Repair = 'repair',
  RepairSameProv = 'repair_same_prov',
  ReturnReplace = 'replace',
  ReturnReproduce = 'reproduce_same_prov',
  Canceled = 'canceled',
}

export enum QualityControlKind {
  XQC = 'xqc',
  VQC = 'vqc',
}

export enum QualityControlState {
  Draft = 'draft',
  Processed = 'processed',
  Canceled = 'canceled',
}

export interface IQualityControlPosition {
  id?: number;
  key?: string;

  claims?: unknown;

  quantity: number;
  customerPrice: number;
  customerPriceMoney?: Money;
  providerPrice: number;
  providerPriceMoney?: Money;

  partDetails: string;
  name: string;
  material: string;
  postProcessing: string;

  partId: number;
  part?: IDealPart;

  lotId?: number;
  lot?: IDealLot;

  measurements?: IMeasurement[];

  measurementsCount?: number;

  temperature?: number;
  humidity?: number;
  atmPressure?: number;

  qtyChecked: number;
  qtyAccepted?: number;
  qtyHold?: number;
  qtyRejected?: number;

  techVerdict?: null | QualityControlTechVerdict;
  techVerdictComment?: string;
  techVerdictPublished?: boolean;

  businessVerdict?: QualityControlBusinessVerdict;
  businessVerdictComment?: string;
  businessVerdictPublished?: boolean;

  officialVerdictComment?: string;

  qccLink?: string | null;

  qcPackaging?: IQCPassedType;
  qcPackagingComment?: string;

  qcDocuments?: IQCPassedType;
  qcDocumentsComment?: string;

  qcConservation?: IQCPassedType;
  qcConservationComment?: string;

  qcQuantity?: IQCPassedType;
  qcQuantityObserved?: number;
  qcQuantityComment?: string;

  qcCompliance?: IQCPassedType;
  qcComplianceComment?: string;

  qcMarking?: IQCPassedType;
  qcMarkingComment?: string;

  qcFinish?: IQCPassedType;
  qcFinishComment?: string;

  qcCoating?: IQCPassedType;
  qcCoatingComment?: string;

  qcOther?: IQCPassedType;
  qcOtherComment?: string;

  providerOrder?: IQualityControlPO;

  providerOrderIdOption?: {
    label?: string;
    value?: number;
  };

  rejectReason?: string | null;

  qcCreatedBy?: {
    by: string;
    at: string;
  };

  positionUpdatedBy?: {
    by: string;
    at: string;
  };

  techVerdictUpdatedBy?: {
    by: string;
    at: string;
  };

  businessVerdictUpdatedBy?: {
    by: string;
    at: string;
  };

  createdAt?: string;
  _destroy?: boolean;
}

export interface IQualityControlPO {
  id: number;
  dealId: number;
  number?: string;
  state: string;
  conformityCheck?: ProviderOrderConformityCheck;
  provider?: IQualityControlPOProvider;
}

export type QualityControl = DealQualityControlQuery['dealQualityControl'];
export type QualityControlPO = NonNullable<QualityControl['positions']>[number]['providerOrder'];

export interface IQualityControlPOProvider {
  id: number;
  name: string;
}

export interface IQualityControlForm {
  dealId: number;
  kind: QualityControlKind;
  reportComment?: string | null;
  internalComment?: string | null;
  positions?: {
    [key: string]: IQualityControlPosition;
  };
}

export interface IQualityControl extends IQualityControlForm {
  id: number;
  dealId: number;
  number?: string | null;
  state: QualityControlState;
  createdAt: string;
}

export interface IMeasurement {
  key?: string;
  id: string | number;
  partId: string | null | undefined;
  qcPositionId: string | null | undefined;
  qcCanvasId: string;
  mark: MeasurementMarkFragment | null | undefined;
  measurements:
    | {
        samples?: IMeasurementSample;
      }
    | null
    | undefined;
  position: number | null | undefined;
  name: string;
  kind: string;
  toleranceClass: string | null | undefined;
  nominalValue: string;
  minValue: string | null | undefined;
  maxValue: string | null | undefined;
  minMaxStr: string | null | undefined;
  units: string | null | undefined;
  toolset: string | null | undefined;
  verdict: string | null | undefined;
  comment: string | null | undefined;
  createdAt: string;
}

export interface IMeasurementMarkCords {
  x: number;
  y: number;
  r: number;
  offsetX: number;
  offsetY: number;
}

export interface IMeasurementMark {
  key: string;
  position: string;
  canvasId: number;
  active: boolean;
  fill: string;
  v: string;
  label: IMeasurementMarkCords;
  point: IMeasurementMarkCords;
}

export interface IMeasurementSample {
  [key: string]: {
    value: string;
  };
}
